import React, { useEffect, useState, useRef } from 'react';
import { useAI } from '../../context/AIContext';
import AIChatTabs from './AIChatTabs';
import AIChatHistory from './AIChatHistory';
import { ChevronUpIcon, ChevronDownIcon } from './icons/AIIcons';
import { surfaces, borders, textColors } from '../../../design-system/tokens';

/**
 * AIChatHeader Component
 * 
 * Header for the AI chat section with tab navigation for recent chats
 * 
 * Props:
 * - collapsed: boolean - whether the chat section is collapsed
 * - onToggle: function - callback when collapse/expand is clicked
 * - websiteId: number - current website ID for filtering chats
 */
const AIChatHeader = ({ collapsed, onToggle, websiteId }) => {
  const { 
    chats, // Use chats directly (recentChats was undefined)
    currentChatId, 
    setCurrentChatId, 
    createChat, 
    fetchChat,
    fetchChats
  } = useAI();
  
  const normalizedWebsiteId =
    typeof websiteId === 'object'
      ? websiteId?.id || websiteId?.website_id || websiteId?.website?.id || websiteId?.value
      : websiteId;

  useEffect(() => {
    if (normalizedWebsiteId) {
      fetchChats(normalizedWebsiteId);
    }
  }, [fetchChats, normalizedWebsiteId]);
  
  // Derive recent chat IDs from chats array (exclude archived + other websites)
  const recentChatIds = (chats || [])
    .filter(c => {
      const chatWebsiteId = typeof c.website === 'object' ? c.website?.id : c.website;
      return String(chatWebsiteId) === String(normalizedWebsiteId) && !c.is_archived;
    })
    .map(c => c.id);
  
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyButtonRef = useRef(null);

  // Handle tab selection
  const handleSelectChat = async (chatId) => {
    setCurrentChatId(chatId);
    await fetchChat(chatId);
    
    // Auto-expand if collapsed
    if (collapsed) {
      onToggle();
    }
  };

  // Handle new chat creation
  const handleNewChat = async () => {
    const chat = await createChat(normalizedWebsiteId, 'New Chat');
    if (chat?.id) {
      // Immediately select and fetch the new chat
      setCurrentChatId(chat.id);
      await fetchChat(chat.id);
      // Refresh chats so generated title appears after first message
      if (normalizedWebsiteId) {
        await fetchChats(normalizedWebsiteId);
      }
    }
    // Auto-expand if collapsed and chat was created
    if (chat && collapsed) {
      onToggle();
    }
  };

  // Handle history modal open
  const handleOpenHistory = () => {
    setHistoryOpen(true);
  };

  return (
    <>
      <div className={`flex items-stretch border-b ${borders.default} ${surfaces.primary} relative`}>
        {/* Tabs section */}
        <div className="flex-1 min-w-0">
          <AIChatTabs
            recentChatIds={recentChatIds}
            currentChatId={currentChatId}
            onSelectChat={handleSelectChat}
            onNewChat={handleNewChat}
            onOpenHistory={handleOpenHistory}
            collapsed={collapsed}
            historyButtonRef={historyButtonRef}
          />
        </div>
        
        {/* Collapse/Expand button */}
        <button 
          className={`px-3 py-2 ${textColors.secondary} ${textColors.hover} ${surfaces.hover} transition-colors border-l ${borders.default} flex-shrink-0`}
          onClick={onToggle}
          aria-label={collapsed ? "Expand AI chat" : "Collapse AI chat"}
        >
          {collapsed ? (
            <ChevronUpIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </button>

        {/* History dropdown - positioned relative to button */}
        <AIChatHistory
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          websiteId={websiteId}
          onSelectChat={handleSelectChat}
          anchorEl={historyButtonRef.current}
        />
      </div>
    </>
  );
};

export default AIChatHeader;
